import { Link } from 'react-router-dom';
import { LoadError } from '../../components/shared/IssuesLoadError';

/**
 * Loading and error states for the issue detail page. Extracted verbatim from
 * IssueDetailPage so the page body is composition-only. A settled failure
 * retains the skeleton without announcing or animating an ongoing load.
 */

/** Skeleton shown while the issue query is in flight (aria-busy region). */
export function IssueDetailSkeleton({ loading = true }: { loading?: boolean }) {
  return (
    <section className={`page-content ${loading ? '' : '[&_.skeleton-shimmer]:animate-none'}`} aria-busy={loading || undefined} aria-label={loading ? 'Loading issue' : undefined} aria-hidden={!loading || undefined}>
      <div className="flex items-center justify-between mb-4">
        <div className="h-4 w-48 rounded skeleton-shimmer" />
        <div className="h-8 w-20 rounded skeleton-shimmer" />
      </div>
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
        <div className="lg:col-span-2 space-y-6">
          <div className="card-sm space-y-6">
            <div className="h-3 w-32 rounded skeleton-shimmer" />
            <div className="h-6 w-3/4 rounded skeleton-shimmer" />
            <div className="h-4 w-full rounded skeleton-shimmer" />
            <div className="h-4 w-5/6 rounded skeleton-shimmer" />
            <div className="flex gap-2 pt-2">
              <div className="h-5 w-16 rounded-full skeleton-shimmer" />
              <div className="h-5 w-20 rounded-full skeleton-shimmer" />
              <div className="h-5 w-14 rounded-full skeleton-shimmer" />
            </div>
          </div>
          <div className="card-sm">
            <div className="h-3 w-24 rounded skeleton-shimmer mb-4" />
            <div className="aspect-video rounded-lg skeleton-shimmer" />
          </div>
        </div>
        <div className="space-y-6">
          <div className="card-sm space-y-3">
            <div className="h-3 w-20 rounded skeleton-shimmer" />
            {[1, 2, 3, 4].map((i) => (
              <div key={i} className="flex items-center gap-3">
                <div className="w-8 h-8 rounded-full skeleton-shimmer flex-shrink-0" style={{ animationDelay: `${i * 60}ms` }} />
                <div className="flex-1">
                  <div className="h-3 rounded skeleton-shimmer" style={{ width: `${[70, 55, 80, 60][i - 1]}%`, animationDelay: `${i * 60}ms` }} />
                </div>
              </div>
            ))}
          </div>
          <div className="card-sm space-y-2">
            <div className="h-3 w-24 rounded skeleton-shimmer" />
            <div className="h-4 w-full rounded skeleton-shimmer" />
            <div className="h-4 w-2/3 rounded skeleton-shimmer" />
          </div>
        </div>
      </div>
    </section>
  );
}

/** Shown when the issue fails to load or was deleted. */
export function IssueDetailError({ error, onRetry, retrying }: { error: Error; onRetry: () => void; retrying?: boolean }) {
  return (
    <>
    <section className="page-content">
      <div className="max-w-xl">
        <LoadError resource="issue" error={error} onRetry={onRetry} retrying={retrying} />
      </div>
      <Link to="/projects" className="text-primary-500 hover:underline mt-4 inline-block">
        <span className="text-cap-trim block">Back to Projects</span>
      </Link>
    </section>
    <IssueDetailSkeleton loading={false} />
    </>
  );
}
